import { IOrderClause } from '@tableau/data-catalog-client-components';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Location } from '~commonInfra/utils/Location';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { WebClientListResourceFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { IActionResource, IListResource } from '~src/httpClient/resources/Resource';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';

import { ConnectedApplicationMenuActions } from '../actions/ConnectedApplicationActions';
import { IConnectedApplicationInfo } from '../models/ConnectedApplicationInfo';
import { ConnectedApplicationInfoMapper } from '../models/ConnectedApplicationInfoMapper';
import { callCreateConnectedApplication, CreateConnectedApplicationResult, CreateConnectedApplicationType } from '../serverApi/requests/CreateConnectedApplication';
import { callCreateConnectedApplicationEas, CreateEasConnectedApplicationError, CreateEasConnectedApplicationResult, CreateEasConnectedApplicationType } from '../serverApi/requests/CreateConnectedApplicationEas';
import { callCreateConnectedApplicationsSecret, CreateConnectedApplicationSecretResult } from '../serverApi/requests/CreateSecret';
import { callDeleteConnectedApplication } from '../serverApi/requests/DeleteConnectedApplication';
import { callDeleteConnectedApplicationEas } from '../serverApi/requests/DeleteConnectedApplicationEas';
import { callDeleteConnectedApplicationsSecret } from '../serverApi/requests/DeleteSecret';
import { GetConnectedApplications, IGetConnectedApplicationsResult } from '../serverApi/requests/GetConnectedApplications';
import { callGetConnectedApplicationsSecret, IGetConnectedApplicationsSecretResult } from '../serverApi/requests/GetSecretValueById';
import { callUpdateConnectedApplication, UpdateConnectedApplicationResult, UpdateConnectedApplicationType } from '../serverApi/requests/UpdateConnectedApplication';
import { callUpdateConnectedApplicationEas, UpdateConnectedApplicationEasType, UpdateEasConnectedApplicationResult } from '../serverApi/requests/UpdateConnectedApplicationEas';

const EAS_LIMIT_EXCEEDED_ERROR_CODE: string = '400161';

function connectedApps(connectedAppsResult: IGetConnectedApplicationsResult): IConnectedApplicationInfo[] {
  var allConnectedApps;
  if ((!connectedAppsResult.directTrustApps) && (!connectedAppsResult.easApps))
    return [];

  if (connectedAppsResult.directTrustApps)
    allConnectedApps =  ConnectedApplicationInfoMapper.mapToDirectTrustInfoType(connectedAppsResult.directTrustApps);
  if (connectedAppsResult.easApps)
    allConnectedApps = allConnectedApps.concat(ConnectedApplicationInfoMapper.mapToExternalAuthorizationServerInfoType(connectedAppsResult.easApps));

  return allConnectedApps;
}

export type ConnectedApplicationsResultMapper = (connectedAppsResult: IGetConnectedApplicationsResult) => IConnectedApplicationInfo[];

class ConnectedApplicationsResource implements IListResource<IConnectedApplicationInfo>, IActionResource<ConnectedApplicationMenuActions> {

  public all(order?: IOrderClause[]): ResourceQuery<IConnectedApplicationInfo> {
    const fetcher = new WebClientListResourceFetcher(GetConnectedApplications, this.getResultMapper(order));
    return new ResourceQuery(fetcher);
  }

  public mapFieldToValue(connectedApplication: IConnectedApplicationInfo, field: keyof IConnectedApplicationInfo): string | number {
    // We want to avoid sorting on the underlying boolean/id fields since the user can't see them and the sort results would make less sense
    switch (field) {
      case 'enabled':
        return connectedApplication.enabled
          ? Messages.connectedApplicationsEnabled()
          : Messages.connectedApplicationsDisabled();
      default:
        return connectedApplication[field];
    }
  }

  public getResultMapper(orderClauses?: IOrderClause[]): ConnectedApplicationsResultMapper {
    return result => {
      let items = connectedApps(result);
      if (orderClauses) {
        // If A > B and sorting ascending then return 1
        items.sort((a, b) => {
          for (let clause of orderClauses) {
            const direction = clause.ascending ? 1 : -1;
            const aVal = this.mapFieldToValue(a, clause.field as keyof IConnectedApplicationInfo);
            const bVal = this.mapFieldToValue(b, clause.field as keyof IConnectedApplicationInfo);
            if (aVal !== bVal) {
              return aVal > bVal ? direction : -direction;
            }
            // If the fields are equal in value we continue to the next one until
            // we either hit a tie-breaker or run out of fields to compare
          }
          return 0;
        });
      }
      return items;
    };
  }

  // This is needed because we use the generic infra for handling delete menu and it
  // doesn't know when to enable or disable. Other types make an actual web request
  // but if you can see it, you can delete it for ConnectedApps
  public actions(): Promise<ConnectedApplicationMenuActions> {
    return Promise.resolve({
      del: true
    });
  }

  public async deleteConnectedAppsDirectTrust(ids: string[]): Promise<IResult> {
    return Promise.all(ids.map(id => callDeleteConnectedApplication(id)
      .catch((e) => {
        return { errors: [{ code: e.data.error.code, id: id }] } as IResult;
      })))
      .then(ResultHelper.combineResults);
  }

  public async deleteConnectedAppsEas(ids: string[]): Promise<IResult> {
    return Promise.all(ids.map(id => callDeleteConnectedApplicationEas(id)
      .catch((e) => {
        return { errors: [{ code: e.data.error.code, id: id }] } as IResult;
      })))
      .then(ResultHelper.combineResults);
  }


  public async create(params: CreateConnectedApplicationType): Promise<CreateConnectedApplicationResult> {
    try {
      const result = await callCreateConnectedApplication({
        connectedApplication: params,
      });
      ToasterService.instance.pop({
        type: ToastType.Success,
        message: Messages.connectedApplicationsConnectedApplicationCreated(),
      });
      return result;
    } catch (e) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        message: Messages.errorUnknown(),
      });
    }
  }

  public async createEas(params: CreateEasConnectedApplicationType): Promise<CreateEasConnectedApplicationResult> {
    try {
      const result = await callCreateConnectedApplicationEas({
        externalAuthorizationServer: params,
      });
      if (result !== undefined) {
        ToasterService.instance.pop({
          type: ToastType.Success,
          message: Messages.connectedApplicationsConnectedApplicationCreated(),
        });
      }
      return result;
    } catch (e) {
      let errorMessage = Messages.errorUnknown();
      let error: CreateEasConnectedApplicationError = e['data']['error'];
      if (error.code === EAS_LIMIT_EXCEEDED_ERROR_CODE) {
        errorMessage = Messages.easLimitExceeded();
      }
      ToasterService.instance.pop({
        type: ToastType.Error,
        message: errorMessage
      });
    }
  }

  public async updateConnectedAppsDirectTrust(params: UpdateConnectedApplicationType): Promise<UpdateConnectedApplicationResult> {
    try {
      const result = await callUpdateConnectedApplication({
        connectedApplication: params,
      });
      if (result !== undefined && !result.error) {
        ToasterService.instance.pop({
          type: ToastType.Success,
          message: Messages.connectedApplicationsConnectedApplicationUpdated(),
        });
      }
      return result;
    } catch (e) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        message: Messages.errorUnknown(),
      });
    }
  }

  public async updateConnectedAppsEas(params: UpdateConnectedApplicationEasType): Promise<UpdateEasConnectedApplicationResult> {
    try {
      const result = await callUpdateConnectedApplicationEas({
        externalAuthorizationServer: params,
      });
      if (result !== undefined) {
        ToasterService.instance.pop({
          type: ToastType.Success,
          message: Messages.connectedApplicationsConnectedApplicationUpdated(),
        });
      }
      return result;
    } catch (e) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        message: Messages.errorUnknown(),
      });
    }
  }


  public navigateToAllConnectedApplicationsPage() {
    const urls = siteUrlsSelector(getAppStore().getState());
    Location.instance.setUrl(urls.settingsConnectedApplicationsUrl());
    getAppStore().dispatch(requestSpaceContents());
  }

  public async deleteSecret(appId: string, secretId: string): Promise<IResult> {
    try {
      const result = await callDeleteConnectedApplicationsSecret(appId, secretId);
      ToasterService.instance.pop({
        type: ToastType.Success,
        message: Messages.connectedApplicationsDeleteSecretSuccessMessage(),
      });
      return result;
    } catch (e) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        message: Messages.connectedApplicationsDeleteSecretFailureMessage(),
      });
    }
  }

  public async getSecret(appId: string, secretId: string): Promise<IGetConnectedApplicationsSecretResult> {
    try {
      const result = await callGetConnectedApplicationsSecret(appId, secretId);
      return result;
    } catch (e) {
    }
  }

  public async createSecret(appId: string): Promise<CreateConnectedApplicationSecretResult> {
    try {
      const result = await callCreateConnectedApplicationsSecret(appId);
      ToasterService.instance.pop({
        type: ToastType.Success,
        message: Messages.connectedApplicationsGenerateSecretSuccessMessage(),
      });
      return result;
    } catch (e) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        message: Messages.connectedApplicationsGenerateSecretFailureMessage(),
      });
    }
  }

  public navigateToConnectedApplicationsDetailsPage(id: string) {
    const urls = siteUrlsSelector(getAppStore().getState());
    Location.instance.setUrl(urls.connectedApplicationDirectTrustUrl(id));
  }

  public navigateToEasConnectedApplicationsDetailsPage(id: string) {
    const urls = siteUrlsSelector(getAppStore().getState());
    Location.instance.setUrl(urls.connectedApplicationEasUrl(id));
  }
}

export const ConnectedApplications = new ConnectedApplicationsResource();
